import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { formatMoney, formatDate, isNegative } from '@/lib/money';
import type { ReferenceData } from '@/features/transactions/types';
import { BudgetForm } from './BudgetForm';
import { consumptionTone } from './types';
import type { BudgetDetail, BudgetRow, BudgetSummary } from './types';

export function BudgetPage({ selectedIds }: { selectedIds: number[] }) {
  const [openId, setOpenId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const queryClient = useQueryClient();

  const budgets = useQuery({
    queryKey: ['budgets', selectedIds],
    queryFn: () => api.get<{ data: BudgetSummary[] }>('/budgets', { entityIds: selectedIds }),
    select: (r) => r.data,
  });

  const reference = useQuery({
    queryKey: ['reference', selectedIds],
    queryFn: () => api.get<{ data: ReferenceData }>('/reference', { entityIds: selectedIds }),
    select: (r) => r.data,
    staleTime: 5 * 60 * 1000,
  });

  const remove = useMutation({
    mutationFn: (id: number) =>
      api.delete<{ message: string }>(`/budgets/${id}`, { entityIds: selectedIds }),
    onSuccess: (res) => {
      window.alert(res.message);
      setOpenId(null);
      refresh();
    },
    onError: (e) => window.alert(e instanceof ApiError ? e.message : 'Erro ao excluir.'),
  });

  function refresh() {
    setCreating(false);
    void queryClient.invalidateQueries({ queryKey: ['budgets'] });
    void queryClient.invalidateQueries({ queryKey: ['budget'] });
  }

  const list = budgets.data ?? [];

  return (
    <div className="mx-auto max-w-6xl">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Orçamento</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Orçado × realizado × comprometido, por competência.
          </p>
        </div>
        <button
          onClick={() => setCreating(true)}
          disabled={!reference.data}
          className="rounded-lg bg-indigo-600 px-3.5 py-2 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:opacity-50"
        >
          + Novo orçamento
        </button>
      </div>

      {budgets.isPending ? (
        <Skeleton />
      ) : budgets.isError ? (
        <p className="mt-5 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800 dark:border-rose-900/50 dark:bg-rose-950/30 dark:text-rose-300">
          Não foi possível carregar os orçamentos.
        </p>
      ) : list.length === 0 ? (
        <div className="mt-8 rounded-xl border border-dashed border-slate-300 p-10 text-center dark:border-slate-700">
          <p className="text-slate-500 dark:text-slate-400">Nenhum orçamento cadastrado.</p>
          <p className="mx-auto mt-1 max-w-md text-sm text-slate-400">
            Um orçamento aqui compara o que você planejou com o que já aconteceu e com o que já está
            comprometido — antes de o mês fechar, não depois.
          </p>
          <button
            onClick={() => setCreating(true)}
            disabled={!reference.data}
            className="mt-3 rounded-lg bg-indigo-600 px-3.5 py-2 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:opacity-50"
          >
            Criar o primeiro orçamento
          </button>
        </div>
      ) : (
        <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {list.map((b) => (
            <button
              key={b.id}
              onClick={() => setOpenId(openId === b.id ? null : b.id)}
              className={`rounded-xl border p-4 text-left transition ${
                openId === b.id
                  ? 'border-indigo-500 bg-indigo-50/50 dark:bg-indigo-950/30'
                  : 'border-slate-200 bg-white hover:border-slate-300 dark:border-slate-800 dark:bg-slate-900'
              }`}
            >
              <p className="truncate font-medium">{b.name}</p>
              <p className="truncate text-xs text-slate-500 dark:text-slate-400">
                {b.is_consolidated ? 'Consolidado' : b.entity_name} ·{' '}
                {formatDate(b.period_start)} a {formatDate(b.period_end)}
              </p>
              <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                {b.items_count} linha(s)
                {b.expense_limit && ` · teto ${formatMoney(b.expense_limit)}`}
              </p>
              <p className="mt-2 text-[11px] text-indigo-600 dark:text-indigo-400">
                {openId === b.id ? '− fechar' : '+ ver acompanhamento'}
              </p>
            </button>
          ))}
        </div>
      )}

      {openId !== null && (
        <BudgetDetailPanel
          budgetId={openId}
          selectedIds={selectedIds}
          onDelete={() => {
            if (window.confirm('Excluir este orçamento?\n\nNenhum lançamento é afetado — orçamento é um limite de referência.')) {
              remove.mutate(openId);
            }
          }}
        />
      )}

      {creating && reference.data && (
        <BudgetForm
          reference={reference.data}
          selectedIds={selectedIds}
          onClose={() => setCreating(false)}
          onSaved={refresh}
        />
      )}
    </div>
  );
}

// -------------------------------------------------------------------

function BudgetDetailPanel({
  budgetId, selectedIds, onDelete,
}: { budgetId: number; selectedIds: number[]; onDelete: () => void }) {
  const detail = useQuery({
    queryKey: ['budget', budgetId, selectedIds],
    queryFn: () => api.get<{ data: BudgetDetail }>(`/budgets/${budgetId}`, { entityIds: selectedIds }),
    select: (r) => r.data,
  });

  if (detail.isPending) {
    return <div className="mt-6 h-64 animate-pulse rounded-xl bg-slate-200 dark:bg-slate-800" />;
  }

  if (detail.isError) {
    return <p className="mt-6 text-sm text-rose-600">Erro ao carregar o orçamento.</p>;
  }

  const { budget, progress, items, totals, managerial_excluded, note } = detail.data;
  const elapsedPercent = Math.round((progress.elapsed_days / progress.total_days) * 100);

  return (
    <div className="mt-6 rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
      <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 p-3.5 dark:border-slate-800">
        <div>
          <h2 className="font-medium">{budget.name}</h2>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {progress.has_ended
              ? 'Período encerrado'
              : progress.has_started
                ? `Dia ${progress.elapsed_days} de ${progress.total_days} · faltam ${progress.remaining_days}`
                : 'Período ainda não começou'}
          </p>
        </div>
        <button
          onClick={onDelete}
          className="ml-auto text-xs text-rose-600 hover:underline dark:text-rose-400"
        >
          excluir
        </button>
      </div>

      {/* Barra do tempo decorrido: consumo só faz sentido comparado a ele. */}
      {progress.has_started && !progress.has_ended && (
        <div className="border-b border-slate-200 px-3.5 py-2.5 dark:border-slate-800">
          <div className="flex items-center gap-3">
            <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
              <div className="h-full rounded-full bg-slate-400" style={{ width: `${elapsedPercent}%` }} />
            </div>
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {elapsedPercent}% do período
            </span>
          </div>
          {totals.consumption_percent !== null && (
            <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">
              {totals.consumption_percent}% do orçamento consumido.{' '}
              {totals.consumption_percent > elapsedPercent + 10 && (
                <span className="text-amber-700 dark:text-amber-400">
                  O gasto está correndo mais rápido que o mês.
                </span>
              )}
            </p>
          )}
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:text-slate-400">
            <tr>
              <th className="px-3 py-2 font-medium">Categoria</th>
              <th className="px-3 py-2 text-right font-medium">Orçado</th>
              <th className="px-3 py-2 text-right font-medium">Realizado</th>
              <th className="px-3 py-2 text-right font-medium">Comprometido</th>
              <th className="px-3 py-2 text-right font-medium">Previsto</th>
              <th className="px-3 py-2 text-right font-medium">Diferença</th>
              <th className="px-3 py-2 text-right font-medium">Consumo</th>
              <th className="px-3 py-2 text-right font-medium">Tendência</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {items.map((row) => <Row key={row.id} row={row} />)}
          </tbody>
          <tfoot className="border-t-2 border-slate-200 font-medium dark:border-slate-700">
            <tr>
              <td className="px-3 py-2">Total</td>
              <td className="px-3 py-2 text-right">{formatMoney(totals.planned)}</td>
              <td className="px-3 py-2 text-right">{formatMoney(totals.actual)}</td>
              <td className="px-3 py-2 text-right">{formatMoney(totals.committed)}</td>
              <td className="px-3 py-2 text-right">{formatMoney(totals.projected)}</td>
              <td
                className={`px-3 py-2 text-right ${
                  isNegative(totals.difference) ? 'text-rose-600 dark:text-rose-400' : ''
                }`}
              >
                {formatMoney(totals.difference)}
              </td>
              <td className="px-3 py-2 text-right">
                {totals.consumption_percent === null ? '—' : `${totals.consumption_percent}%`}
              </td>
              <td className="px-3 py-2 text-right">{formatMoney(totals.trend)}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="space-y-1.5 border-t border-slate-200 p-3.5 text-[11px] text-slate-500 dark:border-slate-800 dark:text-slate-400">
        <p>{note}</p>
        {Number(managerial_excluded) > 0 && (
          <p className="text-amber-700 dark:text-amber-400">
            {formatMoney(managerial_excluded)} em transferências classificadas aparecem no DRE como
            despesa gerencial, mas não consomem orçamento. É por isso que os dois números diferem.
          </p>
        )}
        <p>
          Tendência linear só é aplicada a categorias variáveis — extrapolar o aluguel pela média
          diária diria que ele custa cinco vezes mais.
        </p>
      </div>
    </div>
  );
}

function Row({ row }: { row: BudgetRow }) {
  const tone = consumptionTone(row.consumption_percent);

  const toneClass = {
    ok: '',
    atencao: 'text-amber-600 dark:text-amber-400',
    limite: 'text-orange-600 dark:text-orange-400',
    estouro: 'text-rose-600 dark:text-rose-400 font-medium',
  }[tone];

  return (
    <tr className={row.is_over_budget ? 'bg-rose-50/50 dark:bg-rose-950/15' : ''}>
      <td className="px-3 py-2">
        {row.label}
        {row.behavior === 'fixa' && (
          <span className="ml-1.5 text-[10px] uppercase tracking-wide text-slate-400">fixa</span>
        )}
      </td>
      <td className="whitespace-nowrap px-3 py-2 text-right">{formatMoney(row.planned)}</td>
      <td className="whitespace-nowrap px-3 py-2 text-right">{formatMoney(row.actual)}</td>
      <td className="whitespace-nowrap px-3 py-2 text-right text-slate-500 dark:text-slate-400">
        {formatMoney(row.committed)}
      </td>
      <td className="whitespace-nowrap px-3 py-2 text-right">{formatMoney(row.projected)}</td>
      <td
        className={`whitespace-nowrap px-3 py-2 text-right ${
          isNegative(row.difference) ? 'text-rose-600 dark:text-rose-400' : ''
        }`}
      >
        {formatMoney(row.difference)}
      </td>
      <td className={`whitespace-nowrap px-3 py-2 text-right ${toneClass}`}>
        {row.consumption_percent === null ? '—' : `${row.consumption_percent}%`}
      </td>
      <td className="whitespace-nowrap px-3 py-2 text-right text-slate-500 dark:text-slate-400">
        {formatMoney(row.trend)}
      </td>
    </tr>
  );
}

function Skeleton() {
  return (
    <div className="mt-5 grid animate-pulse gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="h-28 rounded-xl bg-slate-200 dark:bg-slate-800" />
      ))}
    </div>
  );
}
